// Wave 4.x — Team Presence right rail.
//
// An app-wide right-hand navigation panel (Super Admin + Company Admin only)
// that mirrors the left sidebar's behaviour: it collapses HORIZONTALLY to a
// thin avatar rail to reclaim screen width, and expands to a narrow panel
// listing every company member's live status:
//   • green  = online (active)
//   • amber  = away   (connected but idle for a while)
//   • grey   = offline
// Hovering (or focusing) ANY member reveals an action menu:
//   • Active / Offline → Compose mail · View activity
//   • Away             → Compose mail · Log out user · View activity
// "Compose mail" opens the notifications compose dialog pre-addressed to the
// member; "View activity" deep-links to the Activity Logs page filtered to
// that member; "Log out user" force-signs-out an idle member.
// Super Admins are never listed — their presence is private.
//
// Wave 4.07 —
//   • Unified hover action menu (adds "View activity" everywhere).
//   • The Online tab is split into "Active" and "Away" sections by a
//     horizontal bar (in both the expanded panel and the collapsed rail).
//   • The panel auto-refreshes whenever the portal regains focus / becomes
//     visible again, so admins always see current presence.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
  Users, RefreshCw, Mail, PanelRightOpen, PanelRightClose, LogOut, ScrollText, MoreHorizontal, Search, X,
  Inbox, MessageSquare, Bell, Paperclip, Loader2, AlertCircle, Filter, Copy,
} from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import ComposeDialog from '@/components/mail/ComposeDialog';

const ROLE_LABELS = {
  company_admin: 'Company Admin',
  sales_manager: 'Sales Manager',
  sales_person: 'Sales Person',
  user: 'Team Member',
};
const roleLabel = (r) => ROLE_LABELS[r] || 'Team Member';

const DOT = { online: 'bg-emerald-500', away: 'bg-amber-500', offline: 'bg-slate-300' };
const statusLabel = (s) => (s === 'online' ? 'Active' : s === 'away' ? 'Away' : 'Offline');

function onlineSince(iso) {
  if (!iso) return '';
  const start = new Date(iso).getTime();
  if (Number.isNaN(start)) return '';
  const mins = Math.max(0, Math.floor((Date.now() - start) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

// How long a member has been idle (derived from their last activity time).
function idleFor(iso) {
  const d = onlineSince(iso);
  return d ? `idle for ${d}` : 'idle';
}

// Offline members: absolute "last seen" time rendered in Indian Standard
// Time (Asia/Kolkata) so it is consistent regardless of the viewer's device
// timezone (the whole app displays timestamps in IST).
function lastSeenIST(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const t = d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
    return `last seen at ${t}`;
  } catch { return ''; }
}

function initials(name) {
  const s = (name || '').trim();
  if (!s) return '?';
  const p = s.split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || s[0].toUpperCase();
}

function usePresenceCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem('appshell.presence.collapsed');
      if (v === '0') return false;
      if (v === '1') return true;
    } catch { /* noop */ }
    return true; // default collapsed to save horizontal space
  });
  const set = (v) => {
    setCollapsed(v);
    try { localStorage.setItem('appshell.presence.collapsed', v ? '1' : '0'); } catch { /* noop */ }
  };
  return [collapsed, set];
}

function StatusDot({ status }) {
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full shrink-0 ${DOT[status] || DOT.offline}`}
      title={statusLabel(status)}
    />
  );
}

function Avatar({ name, status }) {
  const active = status !== 'offline';
  return (
    <span className="relative inline-flex shrink-0">
      <span
        className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-[11px] font-semibold border ring-2 ring-white ${
          active
            ? 'bg-[var(--diamond-bg)] text-[var(--diamond-icon)] border-[var(--diamond-border)]'
            : 'bg-slate-100 text-slate-500 border-slate-200'
        }`}
      >
        {initials(name)}
      </span>
      <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${DOT[status] || DOT.offline}`} />
    </span>
  );
}

// --- Unread notification load (Mails + Comments + System reminders) --------
// The Team Presence panel surfaces how many unread notifications each member
// is sitting on, so admins can spot who isn't paying heed. The combined total
// drives an inbox count badge; hovering reveals the Mail / Comments / System
// split. Counts come straight from GET /notifications/presence.
function unreadTitle(u) {
  const total = u.unread_total || 0;
  if (total <= 0) return 'No unread notifications';
  const mail = u.unread_mail || 0;
  const comment = u.unread_comment || 0;
  const system = u.unread_system || 0;
  return `${total} unread — Mail ${mail} · Comments ${comment} · System ${system}`;
}

// Inline inbox chip used in the expanded member rows. Hidden at zero.
function UnreadInboxBadge({ u }) {
  const total = u.unread_total || 0;
  if (total <= 0) return null;
  const display = total > 99 ? '99+' : String(total);
  return (
    <span
      className="relative inline-flex items-center gap-1 shrink-0"
      title={unreadTitle(u)}
      aria-label={unreadTitle(u)}
      data-testid={`team-presence-unread-${u.id}`}
    >
      <Inbox size={14} className="text-slate-400" />
      <span
        className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-semibold tabular-nums bg-rose-500 text-white shadow-sm"
        data-testid={`team-presence-unread-count-${u.id}`}
      >
        {display}
      </span>
    </span>
  );
}

// Corner badge overlaid on a collapsed-rail avatar. Hidden at zero.
function RailUnreadBadge({ u }) {
  const total = u.unread_total || 0;
  if (total <= 0) return null;
  const display = total > 9 ? '9+' : String(total);
  return (
    <span
      className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[15px] h-[15px] px-0.5 rounded-full text-[8px] font-semibold tabular-nums bg-rose-500 text-white ring-2 ring-white"
      title={unreadTitle(u)}
      aria-label={unreadTitle(u)}
      data-testid={`team-presence-rail-unread-${u.id}`}
    >
      {display}
    </span>
  );
}

// Copy a member's unread counts to the clipboard so an admin can paste the
// recap into chat. Used by the breakdown card + the drill-down dialog.
async function copyUnreadRecap(u) {
  const total = u.unread_total || 0;
  const text = `${u.name || 'Member'} — ${total} unread notification${total === 1 ? '' : 's'} `
    + `(Mail ${u.unread_mail || 0} · Comments ${u.unread_comment || 0} · System ${u.unread_system || 0})`;
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Recap copied to clipboard');
  } catch {
    toast.error(`Copy failed — ${text}`);
  }
}

// Mail / Comments / System split shown inside a member's hover card.
// Each tile is clickable → opens a READ-ONLY drill-down of that category's
// unread items (viewing there never marks them read for the recipient).
function UnreadBreakdown({ u, onTile }) {
  const total = u.unread_total || 0;
  const rows = [
    ['mail', 'Mail', u.unread_mail || 0, Mail],
    ['comment', 'Comments', u.unread_comment || 0, MessageSquare],
    ['system', 'System', u.unread_system || 0, Bell],
  ];
  if (total <= 0) {
    return (
      <div
        className="mt-1 flex items-center gap-1.5 rounded-md bg-[var(--paper-warm)] px-2 py-1.5 text-[10.5px] text-slate-400"
        data-testid={`team-presence-unread-none-${u.id}`}
      >
        <Inbox size={12} className="text-slate-300" /> No unread notifications
      </div>
    );
  }
  return (
    <div
      className="mt-1 rounded-md bg-[var(--paper-warm)] px-2 py-1.5"
      data-testid={`team-presence-unread-breakdown-${u.id}`}
    >
      <div className="flex items-center gap-1.5 text-[10.5px] font-semibold text-rose-600 mb-1.5">
        <Inbox size={12} /> {total} unread notification{total === 1 ? '' : 's'}
      </div>
      <div className="grid grid-cols-3 gap-1">
        {rows.map(([key, label, n, Icon]) => {
          const clickable = n > 0 && !!onTile;
          return (
            <button
              key={key}
              type="button"
              disabled={!clickable}
              onClick={clickable ? () => onTile(u, key) : undefined}
              title={clickable ? `View ${n} unread ${label.toLowerCase()}` : `No unread ${label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 rounded bg-white border py-1 transition-colors ${
                clickable
                  ? 'border-[var(--divider)] hover:border-rose-300 hover:bg-rose-50/60 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300'
                  : 'border-[var(--divider)] opacity-70 cursor-default'
              }`}
              data-testid={`team-presence-unread-${label.toLowerCase()}-${u.id}`}
            >
              <Icon size={12} className={n > 0 ? 'text-[var(--ink-navy)]' : 'text-slate-300'} />
              <span className={`text-[12px] tabular-nums font-semibold leading-none ${n > 0 ? 'text-[var(--ink-navy)]' : 'text-slate-300'}`}>{n}</span>
              <span className="text-[9px] text-slate-400 leading-none">{label}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => copyUnreadRecap(u)}
        className="mt-1.5 w-full inline-flex items-center justify-center gap-1 rounded border border-[var(--divider)] bg-white py-1 text-[10.5px] font-medium text-slate-600 hover:bg-white hover:border-rose-300 hover:text-rose-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 transition-colors"
        data-testid={`team-presence-copy-recap-${u.id}`}
        title="Copy this member's unread counts to share in chat"
      >
        <Copy size={11} /> Copy recap
      </button>
    </div>
  );
}
// Shared action menu — the buttons shown inside the hover menu for a member.
// Online / offline get Compose + View activity; away members additionally get
// a Log-out action. (Compose is hidden for self.)
function PresenceActionMenu({ u, onCompose, onLogout, onViewActivity, showLogout }) {
  const base = 'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-[13px] transition-colors';
  return (
    <div className="flex flex-col gap-0.5">
      {!u.is_self && (
        <button
          type="button"
          onClick={() => onCompose(u)}
          data-testid={`team-presence-compose-${u.id}`}
          className={`${base} text-[var(--ink-navy)] hover:bg-[var(--paper-warm)]`}
        >
          <Mail size={14} className="text-slate-500" /> Compose mail
        </button>
      )}
      <button
        type="button"
        onClick={() => onViewActivity(u)}
        data-testid={`team-presence-activity-${u.id}`}
        className={`${base} text-[var(--ink-navy)] hover:bg-[var(--paper-warm)]`}
      >
        <ScrollText size={14} className="text-slate-500" /> View activity
      </button>
      {showLogout && !u.is_self && (
        <button
          type="button"
          onClick={() => onLogout(u)}
          data-testid={`team-presence-logout-${u.id}`}
          className={`${base} text-rose-600 hover:bg-rose-50`}
        >
          <LogOut size={14} /> Log out user
        </button>
      )}
    </div>
  );
}

// Expanded list row — hover / focus reveals the action menu.
function PresenceRow({ u, onCompose, onLogout, onViewActivity, onOpenUnread }) {
  const meta = (
    <>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={`truncate text-[13px] ${u.status === 'offline' ? 'text-slate-500' : 'text-[var(--ink-navy)] font-medium'}`}>
          {u.name}
        </span>
        {u.is_self && <span className="text-[9px] uppercase tracking-wide font-semibold text-slate-400 border border-[var(--divider)] rounded px-1">You</span>}
        {u.status === 'away' && !u.is_self && (
          <span className="text-[9px] uppercase tracking-wide font-semibold text-amber-600 border border-amber-200 bg-amber-50 rounded px-1">Away</span>
        )}
      </div>
      <div className="text-[10.5px] text-slate-400 truncate">
        {roleLabel(u.role)}
        {u.status === 'online' && u.online_since ? ` · online since ${onlineSince(u.online_since)}` : ''}
        {u.status === 'away' ? ' · idle' : ''}
      </div>
      {u.status === 'offline' && u.last_seen && (
        <div
          className="text-[10px] text-slate-400 leading-snug mt-0.5"
          data-testid={`team-presence-last-seen-${u.id}`}
        >
          {lastSeenIST(u.last_seen)}
        </div>
      )}
    </>
  );

  // Self → plain, non-interactive row (no actions on yourself).
  if (u.is_self) {
    return (
      <div className="flex items-center gap-2.5 px-3 py-2" data-testid={`team-presence-user-${u.id}`}>
        <StatusDot status={u.status} />
        <div className="flex-1 min-w-0 text-left">{meta}</div>
        <UnreadInboxBadge u={u} />
      </div>
    );
  }

  const showLogout = u.status === 'away';
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          data-testid={`team-presence-user-${u.id}`}
          className="group w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[var(--paper-warm)] focus:outline-none focus-visible:bg-[var(--paper-warm)] transition-colors"
        >
          <StatusDot status={u.status} />
          <div className="flex-1 min-w-0">{meta}</div>
          <UnreadInboxBadge u={u} />
          <MoreHorizontal size={14} className="text-slate-300 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity shrink-0" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="left" align="start" sideOffset={8} collisionPadding={12} avoidCollisions className="w-56 p-1.5 shadow-lg" data-testid={`team-presence-menu-${u.id}`}>
        <div className="px-2 pt-1 pb-1.5 border-b border-[var(--divider)] mb-1">
          <div className="flex items-center gap-1.5 text-[12px] min-w-0">
            <span className="font-medium text-[var(--ink-navy)] truncate">{u.name}</span>
            {u.status === 'away' && (
              <span className="inline-flex items-center gap-1 text-amber-600 font-medium shrink-0" data-testid={`team-presence-idle-for-${u.id}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> {idleFor(u.last_activity)}
              </span>
            )}
          </div>
          <div className="text-[10.5px] text-slate-400 mt-0.5">{roleLabel(u.role)} · {statusLabel(u.status)}</div>
          {u.status === 'online' && u.online_since && (
            <div className="text-[10.5px] text-emerald-600 font-medium mt-0.5" data-testid={`team-presence-online-since-${u.id}`}>
              Online since {onlineSince(u.online_since)}
            </div>
          )}
          <UnreadBreakdown u={u} onTile={onOpenUnread} />
        </div>
        <PresenceActionMenu u={u} onCompose={onCompose} onLogout={onLogout} onViewActivity={onViewActivity} showLogout={showLogout} />
      </HoverCardContent>
    </HoverCard>
  );
}

// Collapsed rail avatar — hover / focus reveals the same action menu.
function RailAvatar({ u, onCompose, onLogout, onViewActivity, onExpand, onOpenUnread }) {
  // Self → tooltip + click to expand the panel.
  if (u.is_self) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onExpand}
              data-testid={`team-presence-rail-avatar-${u.id}`}
              className="relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
            >
              <Avatar name={u.name} status={u.status} />
              <RailUnreadBadge u={u} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <div className="text-xs">
              <div className="font-medium">{u.name} (You)</div>
              <div className="text-slate-300">{statusLabel(u.status)}{u.status === 'online' && u.online_since ? ` · since ${onlineSince(u.online_since)}` : ''}</div>
              <div className="text-slate-300">{unreadTitle(u)}</div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const showLogout = u.status === 'away';
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          data-testid={`team-presence-rail-avatar-${u.id}`}
          className="relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
        >
          <Avatar name={u.name} status={u.status} />
          <RailUnreadBadge u={u} />
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="left" align="start" sideOffset={8} collisionPadding={12} avoidCollisions className="w-56 p-2 shadow-lg" data-testid={`team-presence-rail-menu-${u.id}`}>
        <div className="flex items-center gap-2 mb-1.5">
          <Avatar name={u.name} status={u.status} />
          <div className="min-w-0">
            <div className="text-sm font-medium text-[var(--ink-navy)] truncate">{u.name}</div>
            <div
              className={`text-[11px] font-medium ${u.status === 'away' ? 'text-amber-600' : 'text-emerald-600'}`}
              data-testid={`team-presence-rail-idle-for-${u.id}`}
            >
              {statusLabel(u.status)}{u.status === 'away' ? ` · ${idleFor(u.last_activity)}` : (u.online_since ? ` · since ${onlineSince(u.online_since)}` : '')}
            </div>
            <div className="text-[10px] text-slate-400">{roleLabel(u.role)}</div>
          </div>
        </div>
        <UnreadBreakdown u={u} onTile={onOpenUnread} />
        <div className="mt-1.5" />
        <PresenceActionMenu u={u} onCompose={onCompose} onLogout={onLogout} onViewActivity={onViewActivity} showLogout={showLogout} />
      </HoverCardContent>
    </HoverCard>
  );
}

// ── Read-only unread drill-down ────────────────────────────────────────────
// Opened by clicking a Mail / Comments / System tile. Lists that category's
// UNREAD items for the member with a sender filter. This is a pure read: it
// never marks anything read — the items stay unread in the member's inbox.
const CATEGORY_META = {
  mail: { label: 'Unread Mail', Icon: Mail },
  comment: { label: 'Unread Comments', Icon: MessageSquare },
  system: { label: 'Unread System', Icon: Bell },
  all: { label: 'Unread Notifications', Icon: Inbox },
};
const ITEM_ICON = { mail: Mail, comment: MessageSquare, system: Bell };

function relTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function UnreadItemRow({ it }) {
  const Icon = ITEM_ICON[it.category] || Bell;
  const isSystem = it.sender_id === '__system__';
  return (
    <div
      className={`rounded-md border p-2.5 ${it.priority ? 'border-rose-200 bg-rose-50/40' : 'border-[var(--divider)] bg-white'}`}
      data-testid={`unread-viewer-item-${it.id}`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={13} className="text-slate-500 shrink-0" />
        <span className="text-[12px] font-medium text-[var(--ink-navy)] truncate">
          {isSystem ? 'System' : it.sender_name}
        </span>
        {it.priority && (
          <span className="text-[9px] uppercase tracking-wide font-semibold text-rose-600 border border-rose-200 bg-rose-50 rounded px-1 shrink-0">Priority</span>
        )}
        {it.order_no && (
          <span className="text-[10px] font-mono text-slate-500 bg-slate-100 rounded px-1 shrink-0">{it.order_no}</span>
        )}
        {it.has_attachments && <Paperclip size={11} className="text-slate-400 shrink-0" />}
        <span className="text-[10px] text-slate-400 ml-auto shrink-0">{relTime(it.created_at)}</span>
      </div>
      {(it.subject || it.title) && (
        <div className="text-[12px] font-medium text-[var(--ink-navy)] break-words">{it.subject || it.title}</div>
      )}
      {it.message && (
        <div className="text-[11.5px] text-slate-500 break-words">{it.message}</div>
      )}
    </div>
  );
}

function UnreadViewerDialog({ open, onClose, user, category, companyId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [senderId, setSenderId] = useState('all');

  useEffect(() => {
    if (!open || !user) return undefined;
    setSenderId('all');
    setData(null);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ category: category || 'all' });
        if (companyId) params.set('company_id', companyId);
        const { data: res } = await api.get(`/notifications/presence/${user.id}/unread?${params.toString()}`);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) {
          setData({ items: [], senders: [] });
          toast.error(e.response?.data?.detail || 'Failed to load unread items');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user?.id, category, companyId]);

  const meta = CATEGORY_META[category] || CATEGORY_META.all;
  const CatIcon = meta.Icon;
  const senders = data?.senders || [];
  const allItems = data?.items || [];
  const items = senderId === 'all' ? allItems : allItems.filter((it) => it.sender_id === senderId);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="team-presence-unread-viewer">
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-[var(--divider)] text-left space-y-0">
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-rose-50 text-rose-600 shrink-0">
              <CatIcon size={15} />
            </span>
            <span className="truncate">{meta.label} <span className="text-slate-400 font-normal">· {user?.name || ''}</span></span>
          </DialogTitle>
          <DialogDescription className="text-[11.5px] flex items-start gap-1.5 mt-1.5">
            <AlertCircle size={12} className="text-amber-500 shrink-0 mt-0.5" />
            <span>Read-only — viewing here does <b>not</b> mark these as read. They stay unread in {user?.name ? `${user.name}'s` : 'the'} inbox.</span>
          </DialogDescription>
        </DialogHeader>

        {/* Sender filter */}
        <div className="px-4 py-2.5 border-b border-[var(--divider)] bg-[var(--paper-warm)] flex items-center gap-2">
          <span className="text-[11px] text-slate-500 inline-flex items-center gap-1 shrink-0"><Filter size={12} /> Sender</span>
          <Select value={senderId} onValueChange={setSenderId}>
            <SelectTrigger className="h-8 text-[12px] bg-white" data-testid="unread-viewer-sender-filter">
              <SelectValue placeholder="All senders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="unread-viewer-sender-all">All senders ({allItems.length})</SelectItem>
              {senders.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`unread-viewer-sender-${s.id}`}>
                  {s.name} ({s.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-[11px] text-slate-400 tabular-nums shrink-0 ml-auto" data-testid="unread-viewer-count">
            {items.length} shown
          </span>
        </div>

        {/* Body */}
        <div className="max-h-[52vh] overflow-y-auto px-3 py-2.5 space-y-1.5">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-md border border-[var(--divider)] p-2.5 animate-pulse">
                <div className="h-3 w-1/3 bg-slate-100 rounded mb-2" />
                <div className="h-2.5 w-2/3 bg-slate-100 rounded" />
              </div>
            ))
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-xs text-slate-400" data-testid="unread-viewer-empty">
              <Inbox size={22} className="mx-auto mb-2 text-slate-300" />
              No unread items {senderId === 'all' ? '' : 'from this sender'}.
            </div>
          ) : (
            items.map((it) => <UnreadItemRow key={it.id} it={it} />)
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// A thin horizontal section bar that separates Active / Away members.
function SectionBar({ label, count, testid }) {
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 bg-[var(--paper-warm)]/70 border-y border-[var(--divider)]"
      data-testid={testid}
    >
      <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">{label}</span>
      <span className="inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 rounded-full text-[9px] tabular-nums bg-white text-slate-500 border border-[var(--divider)]">{count}</span>
    </div>
  );
}

export default function TeamPresencePanel() {
  const { effectiveCompanyId, isSuperAdmin, isCompanyAdmin } = useAuth();
  const navigate = useNavigate();
  const enabled = isSuperAdmin || isCompanyAdmin;

  const [collapsed, setCollapsed] = usePresenceCollapsed();
  const [presence, setPresence] = useState({ users: [], online_count: 0, away_count: 0, total: 0 });
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [compose, setCompose] = useState({ open: false, initial: null });
  const [logoutTarget, setLogoutTarget] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [query, setQuery] = useState('');
  // Req 2 — read-only drill-down of a member's unread notifications.
  const [unreadViewer, setUnreadViewer] = useState(null); // { user, category }

  const load = useCallback(async (silent = true) => {
    if (!enabled) return;
    if (!silent) setRefreshing(true);
    try {
      const params = {};
      if (effectiveCompanyId) params.company_id = effectiveCompanyId;
      const { data } = await api.get('/notifications/presence', { params });
      setPresence(data || { users: [], online_count: 0, away_count: 0, total: 0 });
    } catch { /* keep last snapshot */ }
    finally { setLoading(false); setRefreshing(false); }
  }, [enabled, effectiveCompanyId]);

  useEffect(() => {
    if (!enabled) return undefined;
    setLoading(true);
    load(true);
    const t = setInterval(() => load(true), 20000);
    // Wave 4.07 — auto-refresh whenever the portal regains focus / becomes
    // visible again (tab switch, window focus) so the panel is never stale.
    const onFocus = () => { if (document.visibilityState !== 'hidden') load(true); };
    const onVisible = () => { if (document.visibilityState === 'visible') load(true); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, load]);

  if (!enabled) return null;

  const users = presence.users || [];
  const onlineCount = presence.online_count || 0;   // connected (active + away)
  const awayCount = presence.away_count || 0;
  const total = presence.total || 0;
  const offlineCount = Math.max(0, total - onlineCount);
  // Req 1 — sort member names A→Z within every tab/section (online Active +
  // Away sections, the Offline tab, and the All tab).
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
  const connectedUsers = users.filter((u) => u.online);
  const activeUsers = users.filter((u) => u.status === 'online').sort(byName);
  const awayUsers = users.filter((u) => u.status === 'away').sort(byName);
  const offlineUsers = users.filter((u) => !u.online).sort(byName);
  const allUsers = [...users].sort(byName);
  const needsCompany = isSuperAdmin && !effectiveCompanyId;

  // Search filter (name / email / role) applied to the expanded lists.
  const q = query.trim().toLowerCase();
  const matches = (u) => !q
    || (u.name || '').toLowerCase().includes(q)
    || (u.email || '').toLowerCase().includes(q)
    || roleLabel(u.role).toLowerCase().includes(q);

  const startCompose = (u) => {
    if (!u || u.is_self) return;
    setCompose({ open: true, initial: { recipients: [{ id: u.id, name: u.name, email: u.email }] } });
  };
  const viewActivity = (u) => {
    if (!u) return;
    navigate(`/logs?user_id=${encodeURIComponent(u.id)}&user_name=${encodeURIComponent(u.name || '')}`);
  };
  const askLogout = (u) => { if (u && !u.is_self) setLogoutTarget(u); };
  const doLogout = async () => {
    if (!logoutTarget) return;
    setLoggingOut(true);
    try {
      await api.post(`/notifications/presence/${logoutTarget.id}/logout`);
      toast.success(`${logoutTarget.name} has been signed out`);
      setLogoutTarget(null);
      load(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sign out user');
    } finally {
      setLoggingOut(false);
    }
  };

  const rowProps = { onCompose: startCompose, onLogout: askLogout, onViewActivity: viewActivity, onOpenUnread: (u, category) => { if (u) setUnreadViewer({ user: u, category }); } };

  const asideBase = 'hidden md:flex shrink-0 border-l border-[var(--divider)] bg-white sticky top-0 h-screen self-start flex-col transition-[width] duration-200 ease-out z-20';

  let body;
  if (collapsed) {
    // Split the connected avatars into Active then Away, capped for space.
    const combined = [...activeUsers, ...awayUsers];
    const capped = combined.slice(0, 12);
    const extra = combined.length - capped.length;
    const cappedActive = capped.filter((u) => u.status === 'online');
    const cappedAway = capped.filter((u) => u.status === 'away');
    body = (
      <div className="flex flex-col items-center py-2 gap-2 h-full">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setCollapsed(false)}
                data-testid="team-presence-expand"
                className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-[var(--paper-warm)] hover:text-[var(--ink-navy)] transition-colors"
                aria-label="Expand team presence"
              >
                <PanelRightOpen size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">Team Presence — {onlineCount} online{awayCount > 0 ? `, ${awayCount} away` : ''}</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="flex flex-col items-center gap-0.5 text-[10px] text-slate-500" data-testid="team-presence-rail-count" title={`${onlineCount} online · ${total} members`}>
          <span className="relative flex h-2.5 w-2.5">
            {onlineCount > 0 && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${onlineCount > 0 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
          </span>
          <span className="font-mono tabular-nums font-semibold text-[var(--ink-navy)]">{onlineCount}</span>
        </div>

        <div className="w-8 border-t border-[var(--divider)] my-1" />

        {/* pt-2.5 leaves room for the first avatar's -top-1.5 unread badge so it
            isn't clipped by this scroll container's overflow (Team Presence rail). */}
        <div className="flex-1 w-full overflow-y-auto flex flex-col items-center gap-2 pt-2.5">
          {needsCompany ? (
            <Users size={16} className="text-slate-300 mt-2" />
          ) : capped.length === 0 ? (
            <span className="text-[10px] text-slate-400 text-center px-1 mt-1">No one online</span>
          ) : (
            <>
              {cappedActive.map((u) => (
                <RailAvatar key={u.id} u={u} onExpand={() => setCollapsed(false)} {...rowProps} />
              ))}
              {cappedActive.length > 0 && cappedAway.length > 0 && (
                <div className="w-6 border-t border-dashed border-amber-300 my-0.5" data-testid="team-presence-rail-away-divider" title="Away" />
              )}
              {cappedAway.map((u) => (
                <RailAvatar key={u.id} u={u} onExpand={() => setCollapsed(false)} {...rowProps} />
              ))}
              {extra > 0 && (
                <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold border border-slate-200" title={`${extra} more online`}>
                  +{extra}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    );
  } else {
    const emptyState = (
      <div className="px-4 py-8 text-center text-xs text-slate-500" data-testid="team-presence-empty">
        {q
          ? <>No members match “{query.trim()}”.</>
          : <>No {tab === 'all' ? '' : tab} members{tab === 'online' ? ' right now' : ''}.</>}
      </div>
    );
    let listContent;
    if (loading) {
      listContent = Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2.5 px-3 py-2.5">
          <span className="h-2 w-2 rounded-full bg-slate-200" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/2 bg-slate-100 rounded" />
            <div className="h-2.5 w-1/3 bg-slate-100 rounded" />
          </div>
        </div>
      ));
    } else if (tab === 'online') {
      // Split the online tab into Active / Away sections by a horizontal bar.
      const fActive = activeUsers.filter(matches);
      const fAway = awayUsers.filter(matches);
      listContent = (fActive.length + fAway.length) === 0 ? emptyState : (
        <>
          {fActive.length > 0 && <SectionBar label="Active" count={fActive.length} testid="team-presence-section-active" />}
          {fActive.map((u) => <PresenceRow key={u.id} u={u} {...rowProps} />)}
          {fAway.length > 0 && <SectionBar label="Away" count={fAway.length} testid="team-presence-section-away" />}
          {fAway.map((u) => <PresenceRow key={u.id} u={u} {...rowProps} />)}
        </>
      );
    } else {
      const list = (tab === 'offline' ? offlineUsers : allUsers).filter(matches);
      listContent = list.length === 0 ? emptyState : list.map((u) => <PresenceRow key={u.id} u={u} {...rowProps} />);
    }

    body = (
      <>
        <div className="px-3 py-3 border-b border-[var(--divider)] flex items-center justify-between gap-2 shrink-0">
          <div className="min-w-0 flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              {onlineCount > 0 && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />}
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${onlineCount > 0 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            </span>
            <div className="min-w-0">
              <div className="font-medium text-[var(--ink-navy)] leading-tight text-sm">Team Presence</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {needsCompany
                  ? 'Select a company'
                  : (<>{onlineCount} online{awayCount > 0 ? <span className="text-amber-600"> · {awayCount} away</span> : ''} · {total} member{total === 1 ? '' : 's'}</>)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {!needsCompany && (
              <button type="button" onClick={() => load(false)} data-testid="team-presence-refresh" className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-[var(--paper-warm)] hover:text-[var(--ink-navy)] transition-colors" title="Refresh">
                <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
              </button>
            )}
            <button type="button" onClick={() => setCollapsed(true)} data-testid="team-presence-collapse" className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-[var(--paper-warm)] hover:text-[var(--ink-navy)] transition-colors" aria-label="Collapse team presence" title="Collapse">
              <PanelRightClose size={16} />
            </button>
          </div>
        </div>

        {needsCompany ? (
          <div className="px-4 py-8 text-center text-xs text-slate-500" data-testid="team-presence-need-company">
            <Users size={24} className="mx-auto mb-2 text-slate-300" />
            Choose a company from the top switcher to see its team’s live status.
          </div>
        ) : (
          <>
            <div className="px-2.5 py-2 flex items-center gap-1.5 border-b border-[var(--divider)] shrink-0">
              {[['all', 'All', total], ['online', 'Online', onlineCount], ['offline', 'Offline', offlineCount]].map(([key, label, count]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  data-testid={`team-presence-tab-${key}`}
                  aria-pressed={tab === key}
                  className={`inline-flex items-center gap-1 h-7 px-2 rounded-full text-[11px] font-medium border transition-colors ${
                    tab === key
                      ? 'bg-[var(--diamond-bg)] border-[var(--diamond-icon)]/30 text-[var(--ink-navy)]'
                      : 'bg-white border-[var(--divider)] text-slate-500 hover:bg-[var(--paper-warm)] hover:text-[var(--ink-navy)]'
                  }`}
                >
                  {label}
                  <span className={`inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 rounded-full text-[9px] tabular-nums ${tab === key ? 'bg-white/70 text-[var(--ink-navy)]' : 'bg-[var(--paper-warm)] text-slate-500'}`}>{count}</span>
                </button>
              ))}
            </div>

            <div className="px-2.5 py-2 border-b border-[var(--divider)] shrink-0">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search members…"
                  data-testid="team-presence-search"
                  className="w-full h-8 pl-7 pr-7 rounded-md border border-[var(--divider)] bg-white text-[12px] text-[var(--ink-navy)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    data-testid="team-presence-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label="Clear search"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-[var(--divider)]" data-testid="team-presence-list">
              {listContent}
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <aside className={`${asideBase} ${collapsed ? 'w-[56px]' : 'w-[260px]'}`} data-testid="team-presence-panel" data-collapsed={collapsed ? '1' : '0'}>
      {body}

      <ComposeDialog
        open={compose.open}
        onOpenChange={(v) => setCompose((c) => ({ ...c, open: v }))}
        initial={compose.initial}
        onSent={() => setCompose({ open: false, initial: null })}
      />

      <AlertDialog open={!!logoutTarget} onOpenChange={(v) => { if (!v) setLogoutTarget(null); }}>
        <AlertDialogContent data-testid="team-presence-logout-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Log out {logoutTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They’ll be signed out immediately and will need to log in again. Any unsaved work in their session may be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loggingOut}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={(e) => { e.preventDefault(); doLogout(); }} disabled={loggingOut} data-testid="team-presence-logout-confirm-button">
              {loggingOut ? 'Signing out…' : 'Log out user'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnreadViewerDialog
        open={!!unreadViewer}
        onClose={() => setUnreadViewer(null)}
        user={unreadViewer?.user}
        category={unreadViewer?.category}
        companyId={effectiveCompanyId}
      />
    </aside>
  );
}
